// pages/Reviews.js
import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';

const Reviews = () => {
  // Add this useEffect to scroll to top when the component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const [newReview, setNewReview] = useState({
    name: '',
    review: '',
    rating: 0,
    product: '',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  
  // State for reviews
  const [allReviews, setAllReviews] = useState([]);
  
  // API base URL - adjust based on your deployment setup
  const API_URL = '/api'; // This assumes your API is accessible at yourdomain.com/api

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewReview({
      ...newReview,
      [name]: value
    });
  };

  // Handle star rating selection
  const handleRatingSelect = (rating) => {
    setNewReview({
      ...newReview,
      rating
    });
  };

  // Helper function to get current date in the format used in reviews
  const getCurrentDate = () => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June', 
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const date = new Date();
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Fetch reviews from API
  const fetchReviews = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/get_reviews.php`);
      const data = await response.json();
      
      if (data.status === 'success') {
        setAllReviews(data.reviews);
      } else {
        console.error('Error fetching reviews:', data.message);
        // Fallback to default reviews if API fails
        setAllReviews([
          {
            id: 1,
            name: 'Vinayak Saindane',
            review: 'The quality and detail of my 3D printed prototype was absolutely stunning. It helped me secure funding for my product!',
            rating: 5,
            date: '10 April 2025',
            product: 'Geometric Sphere'
          },
          {
            id: 2,
            name: 'Archana Jain',
            review: 'Good service and best product',
            rating: 5,
            date: '10 April 2025',
            product: 'Geometric Sphere'
          },
          // Add more fallback reviews as needed
        ]);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      // Set default reviews on error
      setAllReviews([
        {
          id: 1,
          name: 'Vinayak Saindane',
          review: 'The quality and detail of my 3D printed prototype was absolutely stunning. It helped me secure funding for my product!',
          rating: 5,
          date: '10 April 2025',
          product: 'Geometric Sphere'
        },
        {
          id: 2,
          name: 'Archana Jain',
          review: 'Good service and best product',
          rating: 5,
          date: '10 April 2025',
          product: 'Geometric Sphere'
        },
        // Add more fallback reviews as needed
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate overall rating
  const calculateOverallRating = () => {
    if (allReviews.length === 0) return "0.0";
    const total = allReviews.reduce((sum, review) => sum + parseInt(review.rating), 0);
    return (total / allReviews.length).toFixed(1);
  };
  
  // Count reviews by rating
  const countReviewsByRating = (rating) => {
    return allReviews.filter(review => parseInt(review.rating) === rating).length;
  };
  
  // Calculate rating percentage
  const calculateRatingPercentage = (rating) => {
    if (allReviews.length === 0) return 0;
    const count = countReviewsByRating(rating);
    return Math.round((count / allReviews.length) * 100);
  };

  // Handle review submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newReview.name || !newReview.review || !newReview.rating || !newReview.product) {
      setShowErrorMessage('Please fill in all fields');
      return;
    }
    
    setIsSubmitting(true);
    setShowErrorMessage('');
    
    try {
      // Send review to the PHP API
      const response = await fetch(`${API_URL}/add_review.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newReview.name,
          review: newReview.review,
          rating: newReview.rating,
          product: newReview.product
        })
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        // Show success message
        setShowSuccessMessage(true);
        
        // Add the new review to the reviews list (at the beginning)
        const newReviewObj = data.review;
        setAllReviews(prevReviews => [newReviewObj, ...prevReviews]);
        
        // Reset form
        setNewReview({
          name: '',
          review: '',
          rating: 0,
          product: '',
        });
        
        // Scroll to the top to see the new review
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
        
        // Hide success message after 5 seconds
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 5000);
      } else {
        throw new Error(data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      setShowErrorMessage(error.message || 'Error submitting review. Please try again.');
      
      // Fallback: Add review locally if the API fails
      if (error) {
        const newId = allReviews.length > 0 ? Math.max(...allReviews.map(review => review.id)) + 1 : 1;
        const fallbackReview = {
          id: newId,
          name: newReview.name,
          review: newReview.review,
          rating: newReview.rating,
          date: getCurrentDate(),
          product: newReview.product
        };
        
        setAllReviews([fallbackReview, ...allReviews]);
        
        // Reset form
        setNewReview({
          name: '',
          review: '',
          rating: 0,
          product: '',
        });
        
        // Show success message despite API error
        setShowSuccessMessage(true);
        
        // Scroll to the top to see the new review
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
        
        // Hide success message after 5 seconds
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 5000);
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Fetch reviews when the component mounts
  useEffect(() => {
    fetchReviews();
  }, []);
  
  return (
    <div className="pt-20 pb-16 bg-white">
      {/* Reviews Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-bold text-red-500 mb-6">
            <span className="relative inline-block">
              Customer Reviews
              <span className="absolute -bottom-2 left-0 right-0 h-1 bg-red-500 transform"></span>
            </span>
          </h1>
          <p className="text-xl text-black-400 max-w-3xl mx-auto">
            See what our customers have to say about their experience with our 3D prints.
          </p>
        </div>
        
        {/* Success message for new review - shown at the top */}
        {showSuccessMessage && (
          <div className="bg-green-100 border border-green-500 text-green-700 px-4 py-3 rounded-lg mb-6 animate-pulse">
            <p className="text-center font-medium">Thank you for your review! It has been added below.</p>
          </div>
        )}
        
        {/* Loading indicator */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
          </div>
        )}
        
        {/* Reviews Grid */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allReviews.map((review) => (
              <div key={review.id} className="flex flex-col h-full">
                {/* Added conditional to highlight the newest review */}
                <div className={`bg-gradient-to-br from-red-800 to-black rounded-xl p-6 shadow-lg flex-1 transition-transform hover:scale-[1.02] ${
                  review.id === Math.max(...allReviews.map(r => parseInt(r.id))) ? 'ring-2 ring-red-500' : ''
                }`}>
                  <div className="flex items-start mb-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center bg-red-500 text-white font-bold mr-4 flex-shrink-0">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">{review.name}</h3>
                      <div className="flex items-center mt-1">
                        {[...Array(5)].map((_, i) => (
                          <svg
                            key={i}
                            xmlns="http://www.w3.org/2000/svg"
                            className={`h-4 w-4 ${i < parseInt(review.rating) ? 'text-red-500' : 'text-gray-300'}`}
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                            />
                          </svg>
                        ))}
                        <span className="text-xs text-gray-400 ml-2">• {review.date}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Updated text color for better contrast against the dark background */}
                  <p className="text-gray-300 mb-3">"{review.review}"</p>
                  
                  <div className="mt-auto">
                    <span className="inline-block bg-red-900 text-white px-3 py-1 rounded-full text-xs font-medium">
                      {review.product}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* No reviews message */}
        {!loading && allReviews.length === 0 && (
          <div className="text-center py-16">
            <p className="text-xl text-gray-500">No reviews found. Be the first to share your experience!</p>
          </div>
        )}
      </div>
      
      {/* Overall Rating Stats */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="bg-gradient-to-br from-red-800 to-black bg-opacity-60 backdrop-blur-md rounded-xl overflow-hidden shadow-xl">
          <div className="p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-6 text-center">Overall Rating</h2>
            
            <div className="flex flex-col md:flex-row items-center">
              <div className="w-full md:w-1/3 flex flex-col items-center mb-6 md:mb-0">
                <div className="text-6xl font-bold text-white mb-2">{calculateOverallRating()}</div>
                <div className="flex mb-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg 
                      key={star}
                      className="w-5 h-5 text-red-500" 
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                    </svg>
                  ))}
                </div>
                <div className="font-medium text-gray-400 mt-1">
                  Based on <span className="text-white">{allReviews.length}</span> reviews
                </div>
              </div>
              
              <div className="w-full md:w-2/3 md:pl-10">
                {[5, 4, 3, 2, 1].map((rating) => (
                  <div key={rating} className="flex items-center mb-3">
                    <div className="w-16 text-sm font-medium text-white">{rating} stars</div>
                    <div className="flex-1 mx-3">
                      <div className="h-6 bg-gray-700 rounded-md overflow-hidden shadow-inner">
                        <div 
                          className={`h-full ${rating === 5 ? 'bg-red-600' : 'bg-gray-600'}`}
                          style={{ width: `${calculateRatingPercentage(rating)}%` }}
                        />
                      </div>
                    </div>
                    <div className="w-12 text-right text-sm font-medium text-white">
                      {calculateRatingPercentage(rating)}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Write a Review */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="bg-gradient-to-br from-red-800 to-black backdrop-blur-md rounded-xl overflow-hidden shadow-xl">
          <div className="bg-gradient-to-r from-red-600 to-red-400 py-6 px-8">
            <h2 className="text-2xl font-bold text-white text-center">Share Your Experience</h2>
          </div>
          
          <form className="p-8 space-y-6" onSubmit={handleSubmit}>
            {/* Error message */}
            {showErrorMessage && (
              <div className="bg-red-500 bg-opacity-20 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-6">
                <p className="text-center">{showErrorMessage}</p>
              </div>
            )}
            
            {/* Name field */}
            <div>
              <label htmlFor="name" className="block text-gray-300 mb-2 font-medium">Your Name</label>
              <input
                type="text"
                id="name"
                name="name"
                className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="Enter your name"
                value={newReview.name}
                onChange={handleInputChange}
                required
              />
            </div>
            
            {/* Product selection */}
            <div>
              <label htmlFor="product" className="block text-gray-300 mb-2 font-medium">Product</label>
              <select
                id="product"
                name="product"
                className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                value={newReview.product}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a product</option>
                <option value="Geometric Sphere">Geometric Sphere</option>
                <option value="Abstract Torus">Abstract Torus</option>
                <option value="Modern Cube">Modern Cube</option>
                <option value="Designer Cone">Designer Cone</option>
                <option value="Architectural Tower">Architectural Tower</option>
                <option value="Premium Sculpture">Premium Sculpture</option>
                <option value="Abstract Model">Abstract Model</option>
              </select>
            </div>
            
            {/* Rating selection */}
            <div>
              <label className="block text-gray-300 mb-2 font-medium">Rating</label>
              <div className="flex space-x-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className={`focus:outline-none transition-colors ${
                      star <= newReview.rating ? 'text-red-500' : 'text-gray-400 hover:text-red-400'
                    }`}
                    onClick={() => handleRatingSelect(star)}
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="h-9 w-9" 
                      viewBox="0 0 20 20" 
                      fill="currentColor"
                    >
                      <path 
                        d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" 
                      />
                    </svg>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Review text */}
            <div>
              <label htmlFor="review" className="block text-gray-300 mb-2 font-medium">Your Review</label>
              <textarea
                id="review"
                name="review"
                rows={5}
                className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="Share your experience with our product"
                value={newReview.review}
                onChange={handleInputChange}
                required
              />
            </div>
            
            {/* Submit button */}
            <div className="text-center pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-8 py-3 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white rounded-lg transition-colors shadow-lg font-medium ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Reviews;